import mixpanel from "mixpanel-browser";
import { captureMessage } from "@sentry/browser";
import template from "./notification_settings.html";
import en from "./en.json";
import es from "./es.json";

class NotificationSettingsController {
    /* @ngInject */
    constructor(NoticeSubscriptionService) {
        this.NoticeSubscriptionService = NoticeSubscriptionService;
        this.ALL_FARMS = "all";
        this.SELECTED_FARMS = "selected";
    }

    $onInit() {
        let ctrl = this;
        let growerMembershipsByUser = new Map(
            ctrl.growerMemberships.map((membership) => [
                membership.user,
                membership,
            ]),
        );
        let themesBySid = new Map(
            ctrl.themes.map((theme) => [theme.sid, theme]),
        );
        let farmsBySid = new Map(ctrl.farms.map((farm) => [farm.sid, farm]));
        ctrl.activeFarms = ctrl.farms.filter((farm) => farm.active);
        ctrl.memberships = ctrl.managementMemberships
            .filter((membership) => {
                if (growerMembershipsByUser.has(membership.user)) {
                    return true;
                }
                captureMessage(
                    "Notification administrator is missing a grower membership",
                    {
                        level: "warning",
                        contexts: {
                            membership: {
                                grower_management_membership_sid:
                                    membership.sid,
                                company_sid: ctrl.grower.sid,
                                user_sid: membership.user,
                                active: membership.active,
                            },
                        },
                    },
                );
                return false;
            })
            .map((membership) => {
                let growerMembership = growerMembershipsByUser.get(
                    membership.user,
                );
                return {
                    ...membership,
                    first_name: growerMembership.first_name,
                    last_name: growerMembership.last_name,
                    picture_id: growerMembership.picture_id,
                    name: `${growerMembership.first_name} ${growerMembership.last_name}`.trim(),
                };
            });
        ctrl.entries = ctrl.subscriptions
            .filter(
                (subscription) =>
                    themesBySid.has(subscription.theme) &&
                    growerMembershipsByUser.has(subscription.user),
            )
            .map((subscription) => {
                let growerMembership = growerMembershipsByUser.get(
                    subscription.user,
                );
                return {
                    subscription,
                    theme: themesBySid.get(subscription.theme),
                    membershipSid: subscription.grower_management_membership,
                    recipientName:
                        `${growerMembership.first_name} ${growerMembership.last_name}`.trim(),
                    farmScope: subscription.farm_scope,
                    farms: subscription.farms.map((farmSid) =>
                        farmsBySid.get(farmSid),
                    ),
                };
            });
        ctrl.selectedThemeSid =
            ctrl.themes.length === 1 ? ctrl.themes[0].sid : null;
        ctrl.selectTheme();
    }

    newDraft() {
        let ctrl = this;
        return {
            workerSid: null,
            membershipSid: null,
            farmScope: ctrl.ALL_FARMS,
            farmSids: [],
        };
    }

    selectTheme() {
        let ctrl = this;
        ctrl.selectedTheme = ctrl.themes.find(
            (theme) => theme.sid === ctrl.selectedThemeSid,
        );
        ctrl.draft = ctrl.newDraft();
        ctrl.refreshAvailable();
    }

    refreshAvailable() {
        let ctrl = this;
        let subscribed = new Set(
            ctrl.entries
                .filter((entry) => entry.theme.sid === ctrl.selectedThemeSid)
                .map((entry) => entry.membershipSid),
        );
        ctrl.available = ctrl.selectedTheme
            ? ctrl.memberships.filter(
                  (membership) =>
                      membership.active && !subscribed.has(membership.sid),
              )
            : [];
    }

    selectAdministrator(userSid) {
        let ctrl = this;
        let membership = ctrl.available.find(
            (membership) => membership.user === userSid,
        );
        ctrl.draft.workerSid = userSid || null;
        ctrl.draft.membershipSid = membership ? membership.sid : null;
    }

    subscribe() {
        let ctrl = this;
        let draft = ctrl.draft;
        let theme = ctrl.selectedTheme;
        draft.saving = true;
        draft.errors = null;
        let data = {
            theme: theme.sid,
            grower_management_membership: draft.membershipSid,
            frequency: "",
            farm_scope: draft.farmScope,
            farms:
                draft.farmScope === ctrl.SELECTED_FARMS
                    ? [...draft.farmSids]
                    : [],
        };
        return ctrl.NoticeSubscriptionService.create(data)
            .then((subscription) => {
                let membership = ctrl.memberships.find(
                    (membership) => membership.sid === draft.membershipSid,
                );
                ctrl.entries.push({
                    subscription,
                    theme,
                    membershipSid: draft.membershipSid,
                    recipientName: membership.name,
                    farmScope: draft.farmScope,
                    farms: ctrl.farms.filter((farm) =>
                        data.farms.includes(farm.sid),
                    ),
                });
                ctrl.draft = ctrl.newDraft();
                ctrl.refreshAvailable();
                mixpanel.track("Saved notification subscription", {
                    company_sid: ctrl.grower.sid,
                    theme_identifier: theme.theme_identifier,
                    grower_management_membership_sid: draft.membershipSid,
                    farm_scope: draft.farmScope,
                });
            })
            .catch((reason) => {
                draft.errors = reason.data;
            })
            .finally(() => {
                draft.saving = false;
            });
    }

    unsubscribe(entry) {
        let ctrl = this;
        entry.saving = true;
        entry.errors = null;
        return entry.subscription
            .patch({ active: false })
            .then(() => {
                ctrl.entries = ctrl.entries.filter(
                    (existing) => existing !== entry,
                );
                ctrl.refreshAvailable();
                mixpanel.track("Removed notification subscription", {
                    company_sid: ctrl.grower.sid,
                    theme_identifier: entry.theme.theme_identifier,
                    grower_management_membership_sid: entry.membershipSid,
                });
            })
            .catch((reason) => {
                entry.errors = reason.data;
            })
            .finally(() => {
                entry.saving = false;
            });
    }
}

export default {
    name: "notificationSettings",
    bindings: {
        grower: "<",
        themes: "<",
        managementMemberships: "<",
        growerMemberships: "<",
        subscriptions: "<",
        farms: "<",
        headline: "<?",
    },
    controller: NotificationSettingsController,
    template,
    translations: { en, es },
};
